import { bindable, BindingMode, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './tag-input.html';
import { searchTags } from '../services/api.service';
import type { Tag } from '../services/api.types';

const PAGE_SIZE = 20;

@customElement({ name: 'tag-input', template })
export class TagInput {
  private readonly i18n: I18N = resolve(I18N);
  @bindable({ mode: BindingMode.twoWay }) tags: Tag[] = [];
  @bindable readonly: boolean = false;

  inputValue = '';
  suggestions: Tag[] = [];
  showDropdown = false;
  highlightedIndex = -1;

  private _debounce: ReturnType<typeof setTimeout> | null = null;
  private _currentQuery = '';
  private _currentOffset = 0;
  private _hasMore = false;
  private _loadingMore = false;

  // Portal: dropdown lives on document.body to escape modal overflow/transform
  private _portal: HTMLUListElement | null = null;
  chipsRef!: HTMLElement;
  private _positionHandler = () => this._positionPortal();
  private _touchMoveHandler = (e: TouchEvent) => {
    // Close dropdown on touch-scroll unless the touch is inside the portal itself
    if (this._portal && !this._portal.contains(e.target as Node)) {
      this._clearDropdown();
    }
  };

  attached() {
    this._portal = document.createElement('ul');
    this._portal.className = 'tag-suggestions';
    this._portal.style.display = 'none';
    this._portal.style.position = 'absolute';
    this._portal.style.zIndex = '9999';
    this._portal.addEventListener('mousedown', (e) => this._onPortalMouseDown(e));
    document.body.appendChild(this._portal);
    window.addEventListener('scroll', this._positionHandler, true);
    window.addEventListener('resize', this._positionHandler);
    // iOS: close dropdown when touch-scrolling starts outside the portal
    window.addEventListener('touchmove', this._touchMoveHandler, { capture: true, passive: true });
  }

  detaching() {
    this._portal?.remove();
    this._portal = null;
    window.removeEventListener('scroll', this._positionHandler, true);
    window.removeEventListener('resize', this._positionHandler);
    window.removeEventListener('touchmove', this._touchMoveHandler, true);
  }

  onInput(event: Event) {
    const val = (event.target as HTMLInputElement).value;
    this.inputValue = val;
    this.highlightedIndex = -1;
    if (this._debounce) clearTimeout(this._debounce);
    if (!val.trim()) {
      this._clearDropdown();
      return;
    }
    this._debounce = setTimeout(() => { void this._doSearch(val, 0); }, 200);
  }

  onKeyDown(event: KeyboardEvent): boolean {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.showDropdown && this.suggestions.length > 0) {
        const next = this.highlightedIndex + 1;
        if (next >= this.suggestions.length && this._hasMore && !this._loadingMore) {
          void this._loadMore();
        } else {
          this.highlightedIndex = Math.min(next, this.suggestions.length - 1);
          this._renderPortal();
        }
      }
      return false;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.showDropdown && this.suggestions.length > 0) {
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        this._renderPortal();
      }
      return false;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.showDropdown && this.highlightedIndex >= 0 && this.highlightedIndex < this.suggestions.length) {
        this.addTag(this.suggestions[this.highlightedIndex]);
        return false;
      }
      const val = this.inputValue.trim();
      if (!val) return false;
      const match = this.suggestions.find(s => s.name.toLowerCase() === val.toLowerCase());
      this.addTag(match ?? { id: '', name: val, color: '#6366f1', groupPosition: 9999, position: 0 });
      return false;
    }
    if (event.key === 'Tab') {
      if (this.showDropdown && this.suggestions.length > 0) {
        const idx = this.highlightedIndex >= 0 ? this.highlightedIndex : (this.suggestions.length === 1 ? 0 : -1);
        if (idx >= 0) {
          event.preventDefault();
          this.addTag(this.suggestions[idx]);
          return false;
        }
      }
      return true;
    }
    if (event.key === 'Escape') {
      this._clearDropdown();
      return false;
    }
    if (event.key === ' ' && event.ctrlKey) {
      event.preventDefault();
      void this._doSearch(this.inputValue, 0);
      return false;
    }
    if (event.key === 'Backspace' && this.inputValue === '' && this.tags.length > 0) {
      this.tags = this.tags.slice(0, -1);
      return false;
    }
    return true;
  }

  addTag(tag: Tag) {
    if (!this.tags.some(t => t.name.toLowerCase() === tag.name.toLowerCase())) {
      this.tags = [...this.tags, tag];
    }
    this.inputValue = '';
    this._clearDropdown();
  }

  removeTag(tag: Tag) {
    this.tags = this.tags.filter(t => t.name !== tag.name);
  }

  hideDropdown() {
    setTimeout(() => this._clearDropdown(), 150);
  }

  private async _doSearch(query: string, offset: number) {
    this._currentQuery = query;
    const { tags, hasMore } = await searchTags(query, PAGE_SIZE, offset);
    if (query !== this._currentQuery) return; // stale
    const filtered = tags.filter(s => !this.tags.some(t => t.name.toLowerCase() === s.name.toLowerCase()));
    if (offset === 0) {
      this.suggestions = filtered;
      this.highlightedIndex = -1;
    } else {
      this.suggestions = [...this.suggestions, ...filtered];
    }
    this._hasMore = hasMore;
    this._currentOffset = offset + filtered.length;
    this.showDropdown = this.suggestions.length > 0;
    this._renderPortal();
  }

  private async _loadMore() {
    if (this._loadingMore || !this._hasMore) return;
    this._loadingMore = true;
    const prevLen = this.suggestions.length;
    await this._doSearch(this._currentQuery, this._currentOffset);
    this.highlightedIndex = prevLen; // point to first new item
    this._loadingMore = false;
    this._renderPortal();
  }

  private _clearDropdown() {
    this.showDropdown = false;
    this.highlightedIndex = -1;
    this.suggestions = [];
    this._hasMore = false;
    this._currentOffset = 0;
    this._currentQuery = '';
    this._renderPortal();
  }

  private _positionPortal() {
    if (!this._portal || !this.chipsRef || !this.showDropdown) return;
    const rect = this.chipsRef.getBoundingClientRect();
    const scrollTop = window.scrollY ?? document.documentElement.scrollTop;
    const scrollLeft = window.scrollX ?? document.documentElement.scrollLeft;
    this._portal.style.top = `${rect.bottom + scrollTop + 4}px`;
    this._portal.style.left = `${rect.left + scrollLeft}px`;
    this._portal.style.width = `${rect.width}px`;
  }

  private _renderPortal() {
    if (!this._portal) return;
    if (!this.showDropdown || this.suggestions.length === 0) {
      this._portal.style.display = 'none';
      return;
    }
    this._positionPortal();
    this._portal.style.display = '';
    this._portal.innerHTML = this.suggestions.map((s, i) => {
      const active = i === this.highlightedIndex ? ' tag-suggestion-item--active' : '';
      const label = s.searchText
        ? `${this._esc(s.name)} <span class="tag-suggestion-search-text">(${this._esc(s.searchText)})</span>`
        : this._esc(s.name);
      return `<li class="tag-suggestion-item${active}" data-idx="${i}">` +
        `<span class="tag-color-dot" style="background-color:${this._esc(s.color || '#6366f1')};"></span>` +
        `${label}</li>`;
    }).join('') + (this._hasMore
      ? `<li class="tag-suggestion-load-more">${this._loadingMore ? this.i18n.tr('common.loading') : this.i18n.tr('tags.loadMore')}</li>`
      : '');
    const activeEl = this._portal.querySelector<HTMLElement>('.tag-suggestion-item--active');
    activeEl?.scrollIntoView({ block: 'nearest' });
  }

  private _onPortalMouseDown(e: MouseEvent) {
    const li = (e.target as Element).closest('[data-idx]') as HTMLElement | null;
    if (!li) return;
    e.preventDefault();
    const idx = parseInt(li.dataset['idx'] ?? '-1', 10);
    if (idx >= 0 && idx < this.suggestions.length) this.addTag(this.suggestions[idx]);
  }

  private _esc(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

